import { useState, type FormEvent } from "react";
import {
  submitBetaSignup,
  validateBetaSignup,
  type BetaSignup,
  type BetaSignupErrors,
} from "../beta/betaSignup.js";

const signupEndpoint = import.meta.env.VITE_BETA_SIGNUP_URL?.trim() ?? "";
const DEFAULT_BETA_PREVIEW_VIDEO_URL =
  "https://pub-71b1534587c6464a9b8704ed431535d5.r2.dev/onda_tropical_dancer_5x.mp4";
const demoVideoUrl =
  import.meta.env.VITE_BETA_PREVIEW_VIDEO_URL?.trim() || DEFAULT_BETA_PREVIEW_VIDEO_URL;

const initialSignup: BetaSignup = {
  email: "",
  ageGroup: "",
  usResident: false,
  device: "iphone",
  display: "laptop",
  feedbackAvailability: "yes",
  consent: false,
};

/**
 * Public research landing page for P1. It deliberately lives at /beta while
 * / remains reserved for the M5 desktop-host route from PLAN.md.
 */
export function BetaLandingPage() {
  const [signup, setSignup] = useState<BetaSignup>(initialSignup);
  const [errors, setErrors] = useState<BetaSignupErrors>({});
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [submitError, setSubmitError] = useState<string | undefined>(undefined);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const nextErrors = validateBetaSignup(signup);
    setErrors(nextErrors);
    setSubmitError(undefined);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }
    if (!signupEndpoint) {
      setStatus("error");
      setSubmitError("Beta sign-ups are not connected in this environment yet.");
      return;
    }

    setStatus("submitting");
    try {
      await submitBetaSignup(signup, signupEndpoint);
      setStatus("success");
    } catch (error) {
      setStatus("error");
      setSubmitError(
        error instanceof Error ? error.message : "The beta form could not be submitted.",
      );
    }
  }

  return (
    <main className="beta" data-testid="beta-landing">
      <section className="beta__hero" aria-labelledby="beta-title">
        <div className="beta__copy">
          <p className="beta__eyebrow">Closed beta · US</p>
          <h1 id="beta-title">Make your living room the dance floor.</h1>
          <p className="beta__lede">
            Follow a short dance on your laptop or TV. Your phone checks your movement locally and
            turns each attempt into a score, combo, and a reason to try again.
          </p>
          <div className="beta__facts" aria-label="How Dance Game works">
            <span>1. Open the game</span>
            <span>2. Prop up your phone</span>
            <span>3. Dance and get feedback</span>
          </div>
        </div>

        <div className="beta__demo">
          <video
            className="beta__video"
            controls
            muted
            playsInline
            preload="metadata"
            src={demoVideoUrl}
            aria-label="Onda tropical dancer video preview"
          />
          <p>Reference video preview. Your camera video is never uploaded or stored by the game.</p>
        </div>
      </section>

      <section className="beta__details" aria-label="Beta details">
        <article>
          <h2>Built for a low-pressure dance break</h2>
          <p>
            No console, app-store install, or dance experience required. The first beta focuses on a
            quick solo session: a laptop or TV for the dance, and a phone placed a few feet away.
          </p>
        </article>
        <article>
          <h2>Private by design</h2>
          <p>
            Pose tracking runs in your browser on your phone. The game does not send raw camera
            video to a server or another screen.
          </p>
        </article>
        <article>
          <h2>Help make the score fair</h2>
          <p>
            Beta testers play one short track and share a few minutes of feedback about setup,
            movement tracking, and whether the score felt fair.
          </p>
        </article>
      </section>

      <section className="beta__signup" aria-labelledby="beta-signup-title">
        <div>
          <p className="beta__eyebrow">Free, invite-only test</p>
          <h2 id="beta-signup-title">Request beta access</h2>
          <p>
            We will only use these details to choose a compatible test setup and send a beta invite.
            You can ask to be removed at any time. Please do not submit camera footage or health
            data.
          </p>
        </div>

        {status === "success" ? (
          <p className="beta__success" role="status" data-testid="beta-success">
            You&apos;re on the beta list. Watch your inbox for an invite and setup details.
          </p>
        ) : (
          <form
            className="beta-form"
            onSubmit={(event) => void handleSubmit(event)}
            noValidate
            aria-label="Beta access request"
          >
            <label>
              Email
              <input
                type="email"
                autoComplete="email"
                value={signup.email}
                onChange={(event) =>
                  setSignup((current) => ({ ...current, email: event.target.value }))
                }
                aria-invalid={Boolean(errors.email)}
                aria-describedby={errors.email ? "beta-email-error" : undefined}
                required
              />
              {errors.email ? <span id="beta-email-error">{errors.email}</span> : null}
            </label>

            <label>
              Age range
              <select
                value={signup.ageGroup}
                onChange={(event) =>
                  setSignup((current) => ({
                    ...current,
                    ageGroup: event.target.value as BetaSignup["ageGroup"],
                  }))
                }
                aria-invalid={Boolean(errors.ageGroup)}
              >
                <option value="">Select one</option>
                <option value="18-24">18–24</option>
                <option value="25-30">25–30</option>
                <option value="outside-target">Outside this range</option>
              </select>
              {errors.ageGroup ? <span>{errors.ageGroup}</span> : null}
            </label>

            <label className="beta-form__consent">
              <input
                type="checkbox"
                checked={signup.usResident}
                onChange={(event) =>
                  setSignup((current) => ({ ...current, usResident: event.target.checked }))
                }
              />
              I live in the United States and am at least 18 years old.
            </label>
            {errors.usResident ? <span>{errors.usResident}</span> : null}

            <label>
              Camera device
              <select
                value={signup.device}
                onChange={(event) =>
                  setSignup((current) => ({
                    ...current,
                    device: event.target.value as BetaSignup["device"],
                  }))
                }
              >
                <option value="iphone">iPhone</option>
                <option value="android">Android phone</option>
                <option value="laptop-webcam">Laptop webcam</option>
                <option value="desktop-webcam">Desktop webcam</option>
                <option value="other">Other / not sure</option>
              </select>
              {errors.device ? <span>{errors.device}</span> : null}
            </label>

            <label>
              Game screen available
              <select
                value={signup.display}
                onChange={(event) =>
                  setSignup((current) => ({
                    ...current,
                    display: event.target.value as BetaSignup["display"],
                  }))
                }
              >
                <option value="laptop">Laptop</option>
                <option value="tv">TV or monitor</option>
                <option value="both">Both</option>
                <option value="neither">Neither yet</option>
              </select>
              {errors.display ? <span>{errors.display}</span> : null}
            </label>

            <fieldset>
              <legend>Could you send 5 minutes of feedback after one dance?</legend>
              <label className="beta-form__choice">
                <input
                  type="radio"
                  name="feedback"
                  checked={signup.feedbackAvailability === "yes"}
                  onChange={() =>
                    setSignup((current) => ({ ...current, feedbackAvailability: "yes" }))
                  }
                />
                Yes, I can
              </label>
              <label className="beta-form__choice">
                <input
                  type="radio"
                  name="feedback"
                  checked={signup.feedbackAvailability === "maybe"}
                  onChange={() =>
                    setSignup((current) => ({ ...current, feedbackAvailability: "maybe" }))
                  }
                />
                Maybe
              </label>
              {errors.feedbackAvailability ? <span>{errors.feedbackAvailability}</span> : null}
            </fieldset>

            <label className="beta-form__consent">
              <input
                type="checkbox"
                checked={signup.consent}
                onChange={(event) =>
                  setSignup((current) => ({ ...current, consent: event.target.checked }))
                }
              />
              I agree to receive email about this beta test.
            </label>
            {errors.consent ? <span>{errors.consent}</span> : null}

            {submitError ? (
              <p className="alert alert--error" role="alert" data-testid="beta-submit-error">
                {submitError}
              </p>
            ) : null}
            {!signupEndpoint ? (
              <p className="beta-form__endpoint-note" data-testid="beta-endpoint-note">
                Beta intake is awaiting its secure collection endpoint.
              </p>
            ) : null}
            <button type="submit" disabled={status === "submitting" || !signupEndpoint}>
              {status === "submitting" ? "Requesting access…" : "Request beta access"}
            </button>
          </form>
        )}
      </section>
    </main>
  );
}
